/**
 * Cognito sin SDK: USER_PASSWORD_AUTH y REFRESH_TOKEN_AUTH contra la API JSON de cognito-idp.
 * Se guarda el ID token (lleva custom:tenant_id y `aud` = client id, lo que valida el JWT authorizer).
 * Funciones puras + almacenamiento; el estado reactivo vive en stores/auth.ts.
 */
const REGION = import.meta.env.VITE_REGION || "us-east-1";
const CLIENT_ID = import.meta.env.VITE_USER_POOL_CLIENT_ID ?? "";
export const SESSION_KEY = "pp.session";

export interface Session {
  idToken: string;
  refreshToken: string;
  /** `exp` del ID token, en segundos epoch. */
  exp: number;
}

export interface SessionUser {
  email: string;
  tenantId: string;
  /** `cognito:groups` del ID token. `admin` edita los prompts globales y usa el laboratorio. */
  groups: string[];
  isAdmin: boolean;
}

export const ADMIN_GROUP = "admin";

/** Error de Cognito con su `__type` (NotAuthorizedException, UserNotFoundException...). */
export class CognitoError extends Error {
  constructor(
    public code: string,
    message: string,
  ) {
    super(message);
    this.name = "CognitoError";
  }
}

interface AuthResult {
  IdToken: string;
  RefreshToken?: string;
}

async function initiateAuth(
  flow: "USER_PASSWORD_AUTH" | "REFRESH_TOKEN_AUTH",
  params: Record<string, string>,
  clientMetadata?: Record<string, string>,
): Promise<{ AuthenticationResult?: AuthResult; ChallengeName?: string }> {
  let res: Response;
  try {
    res = await fetch(`https://cognito-idp.${REGION}.amazonaws.com/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-amz-json-1.1",
        "X-Amz-Target": "AWSCognitoIdentityProviderService.InitiateAuth",
      },
      body: JSON.stringify({
        AuthFlow: flow,
        ClientId: CLIENT_ID,
        AuthParameters: params,
        ...(clientMetadata ? { ClientMetadata: clientMetadata } : {}),
      }),
    });
  } catch {
    throw new CognitoError("NetworkError", "No hay conexión. Revisa tu internet e inténtalo de nuevo.");
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const code =
      String(data.__type ?? "")
        .split("#")
        .pop() || `HTTP${res.status}`;
    throw new CognitoError(code, data.message ?? `Cognito ${res.status}`);
  }
  return data;
}

export function decodeClaims(token: string): Record<string, unknown> {
  const part = token.split(".")[1] ?? "";
  const b64 = part
    .replace(/-/g, "+")
    .replace(/_/g, "/")
    .padEnd(Math.ceil(part.length / 4) * 4, "=");
  const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
  return JSON.parse(new TextDecoder().decode(bytes));
}

export function sessionFrom(result: AuthResult, previousRefreshToken = ""): Session {
  return {
    idToken: result.IdToken,
    refreshToken: result.RefreshToken ?? previousRefreshToken,
    exp: Number(decodeClaims(result.IdToken).exp),
  };
}

/** `cognito:groups` llega como array en el ID token; se acepta tambien el texto "[admin otro]" que ve el backend. */
export function parseGroups(raw: unknown): string[] {
  if (Array.isArray(raw)) return raw.map(String);
  if (typeof raw !== "string") return [];
  return raw
    .replace(/[[\]",]/g, " ")
    .split(/\s+/)
    .filter(Boolean);
}

export function userFrom(session: Session): SessionUser {
  const claims = decodeClaims(session.idToken);
  const groups = parseGroups(claims["cognito:groups"]);
  return {
    email: String(claims.email ?? ""),
    tenantId: String(claims["custom:tenant_id"] ?? ""),
    groups,
    isAdmin: groups.includes(ADMIN_GROUP),
  };
}

/** Segundos que le quedan al ID token. */
export function secondsLeft(session: Session, nowMs = Date.now()): number {
  return session.exp - Math.floor(nowMs / 1000);
}

/** Clave de ClientMetadata que lee el trigger Pre Authentication (functions/cognito_pre_auth). */
export const TURNSTILE_METADATA_KEY = "turnstile_token";

/**
 * USER_PASSWORD_AUTH. `turnstileToken` (widget de Cloudflare, web/src/lib/turnstile.ts) viaja en ClientMetadata y
 * el trigger Pre Authentication lo valida antes de la contrasena; vacio = no se manda (Turnstile desactivado).
 */
export async function passwordSignIn(email: string, password: string, turnstileToken = ""): Promise<Session> {
  const data = await initiateAuth(
    "USER_PASSWORD_AUTH",
    { USERNAME: email, PASSWORD: password },
    turnstileToken ? { [TURNSTILE_METADATA_KEY]: turnstileToken } : undefined,
  );
  if (!data.AuthenticationResult) {
    throw new CognitoError(data.ChallengeName ?? "Challenge", `Challenge no soportado: ${data.ChallengeName}`);
  }
  return sessionFrom(data.AuthenticationResult);
}

export async function refreshSession(session: Session): Promise<Session> {
  const data = await initiateAuth("REFRESH_TOKEN_AUTH", { REFRESH_TOKEN: session.refreshToken });
  if (!data.AuthenticationResult) throw new CognitoError("RefreshFailed", "no se pudo renovar la sesión");
  return sessionFrom(data.AuthenticationResult, session.refreshToken);
}

/** Mensaje para la pantalla de login. */
export function signInErrorMessage(err: unknown): string {
  if (err instanceof CognitoError) {
    if (["NotAuthorizedException", "UserNotFoundException", "InvalidParameterException"].includes(err.code)) {
      return "Correo o contraseña incorrectos.";
    }
    if (err.code === "NEW_PASSWORD_REQUIRED") {
      return "Tu cuenta necesita una contraseña nueva. Pide que te la restablezcan.";
    }
    if (err.code === "TooManyRequestsException" || err.code === "LimitExceededException") {
      return "Demasiados intentos. Espera un momento y vuelve a probar.";
    }
    if (err.code === "UserLambdaValidationException") {
      // El trigger Pre Authentication rechazo el token de Turnstile ("PreAuthentication failed with error ...").
      return "No pudimos comprobar que eres una persona. Vuelve a intentarlo.";
    }
    return err.message;
  }
  return err instanceof Error ? err.message : String(err);
}

// ------------------------------------------------------------------ almacenamiento ---
// localStorage puede lanzar (modo privado, cuotas): la sesion queda solo en memoria.

export function loadSession(): Session | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    const parsed = raw ? (JSON.parse(raw) as Session) : null;
    return parsed?.idToken ? parsed : null;
  } catch {
    return null;
  }
}

export function saveSession(session: Session): void {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } catch {
    /* sin almacenamiento */
  }
}

export function clearSession(): void {
  try {
    localStorage.removeItem(SESSION_KEY);
  } catch {
    /* sin almacenamiento */
  }
}
